import type { ChatMessage, ContentBlock } from '../types'
import {
  extractToolOutputsForAssistantMessage,
  isToolResultMessage,
  type ToolOutputs,
} from './extractArtifacts'

// One bubble to render. `outputs` carries the tool results/artifacts for an
// assistant bubble; user bubbles leave it undefined.
export interface RenderUnit {
  key: string
  message: ChatMessage
  outputs?: ToolOutputs
  showBootHeader: boolean
}

// Render unit for a single persisted assistant message — the non-merged case,
// shared by the per-step path and a one-message turn so the unit shape lives
// in one place.
function assistantUnitForIndex(
  messages: ChatMessage[],
  index: number,
  firstAssistantId: number | null,
): RenderUnit {
  const message = messages[index]
  return {
    key: `${message.id}-${message.position}`,
    message,
    outputs: extractToolOutputsForAssistantMessage(messages, index),
    showBootHeader: message.id === firstAssistantId,
  }
}

// A multi-step turn persists one assistant row per model call, with the tool
// results in interleaved (invisible) user messages. With diagnostics off there
// are no per-step usage chips to anchor those separate bubbles, so the steps
// just read as a continuous response broken up by uneven gaps. Merging the
// turn's assistant messages into one bubble (tool results unioned — tool-use
// ids are globally unique) makes the persisted view match the live draft: a
// single bubble whose tool chips collapse into one evenly-spaced cluster. With
// diagnostics on, every step keeps its own bubble + usage chip.
export function buildRenderUnits(
  messages: ChatMessage[],
  firstAssistantId: number | null,
  mergeAssistantTurns: boolean,
): RenderUnit[] {
  const units: RenderUnit[] = []
  let assistantGroup: number[] = []

  function flushAssistantGroup() {
    if (assistantGroup.length === 0) return
    if (mergeAssistantTurns) {
      units.push(
        mergedAssistantUnit(messages, assistantGroup, firstAssistantId),
      )
    } else {
      for (const index of assistantGroup) {
        units.push(assistantUnitForIndex(messages, index, firstAssistantId))
      }
    }
    assistantGroup = []
  }

  messages.forEach((message, index) => {
    if (message.role === 'assistant') {
      assistantGroup.push(index)
      return
    }
    // Tool-result messages are invisible and live between a turn's steps —
    // skip them so they don't break the assistant grouping.
    if (isToolResultMessage(message)) return
    // A real user message ends the previous turn.
    flushAssistantGroup()
    units.push({
      key: `${message.id}-${message.position}`,
      message,
      showBootHeader: false,
    })
  })
  flushAssistantGroup()
  return units
}

function mergedAssistantUnit(
  messages: ChatMessage[],
  group: number[],
  firstAssistantId: number | null,
): RenderUnit {
  const first = messages[group[0]]
  if (group.length === 1) {
    return assistantUnitForIndex(messages, group[0], firstAssistantId)
  }

  const blocks: ContentBlock[] = []
  const artifacts: ToolOutputs['artifacts'] = {}
  const toolResults: ToolOutputs['toolResults'] = {}
  let showBootHeader = false
  for (const index of group) {
    const message = messages[index]
    blocks.push(...message.contentJson)
    const outputs = extractToolOutputsForAssistantMessage(messages, index)
    Object.assign(artifacts, outputs.artifacts)
    Object.assign(toolResults, outputs.toolResults)
    if (message.id === firstAssistantId) showBootHeader = true
  }

  // Synthetic bubble: every step's blocks concatenated. Per-message usage /
  // model are dropped — the usage chip is diagnostics-only and we're in the
  // diagnostics-off branch, so there's nothing to attribute per step here.
  const merged: ChatMessage = {
    ...first,
    contentJson: blocks,
    contentText: null,
    model: null,
    usage: null,
    costMicros: null,
  }
  return {
    key: `${first.id}-${first.position}`,
    message: merged,
    outputs: { artifacts, toolResults },
    showBootHeader,
  }
}
